// EnergySettingsV2 — a wallet's STANDING ORDER, label and status, as its own page (/energy/settings/<id>) (UI rebuild batch 4: the
// UI-rebuild lane's hand-off of 27 Oct 2026, adopted by the energy lane; spec docs/ui-rebuild/handoff/ENERGY.md on
// ui-rebuild/batch4-handoff). Astra's `energy-settings`: "move standing energy and transfers-per-day to a dedicated wallet settings
// view" — off the monitoring list, where Energy.tsx unfolds it under the row.
//
// ONE COPY: the state and every rule are screens/Energy.tsx's useEditWallet — the label and standing fields, transfers a day, the
// estimate from the desk's price board (dated), Save sending ONLY what changed, Pause / Make active (held back by the register's
// no) / Retire (confirmed), and "Arrange N now": the SAVED figure, never one typed beside it, one key per figure kept until the
// desk answers, so a press with no answer goes out again as the same order. What is this page's own: the layout, and Astra's
// commitment boundary — "Review settings" shows what will change (Save's own checks, useEditWallet.checked), then Save sends it.
//
// Full access only, as Energy.tsx's Edit button is: a read-only admin is sent back to the list with one line.
//
// ONE FACT A LINE (his words, 1 Nov 2026: "check everything where it applies"): the wallet's name and its address are two facts;
// beside the form, its energy now, what is rented and when that ends are one each.
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { EnergyStatus, EnergyWallet } from '../../../lib/api';
import { errorWords } from '../../../lib/errors';
import { Fact, StateLine } from '../../../components/v2/kit';
import { Field, FieldRow } from '../../../components/Field';
import { FormAside, FormFoot, FormFrame, PRIMARY, QUIET } from '../../../components/v2/move/FormFrame';
import { fmtStamp } from '../../../lib/format';
import { DeskLines, EnergyFigure, EnergyNow, StateWord, ToneWord, V2_SLABS, besideForm, hasReading, underForm } from '../../../components/v2/energy/parts';
import {
  ARRANGE_UNSAVED, AboutStanding, ArrangeNoAnswer, EditHold, Outcome, Said, StandingCost, TransfersADay,
  STATE_TONE, STATE_WORD, endsIn, lasts, mask, secondsUntil, standingRenews, units, useDesk, useEditWallet, nameOf
} from '../../Energy';

export function EnergySettingsV2() {
  const desk = useDesk();
  const { d, canChange } = desk;
  const { walletId } = useParams();
  const w = d?.wallets.find((x) => String(x.id) === walletId) ?? null;
  const back = <Link to="/energy" className="tap-44 btn-link text-meta">← Energy</Link>;

  const aside = d && w && (
    <FormAside
      title="Standing order"
      figure={Number(w.standingEnergy) > 0 ? units(w.standingEnergy) : 'None'}
      sub={Number(w.standingEnergy) === 0 ? 'No energy kept on this wallet.'
        : standingRenews(d, w) ? `Renewed by the desk, ${lasts(d.policy.standingDurationSec)} at a time.`
          : w.status !== 'active' ? `Not renewed while the wallet is ${w.status}.` : 'Not renewed by itself — Arrange rents it once.'}
      facts={[
        { label: 'In place', value: <EnergyFigure n={w.standingCovered} /> },
        { label: 'Energy now', value: <EnergyNow w={w} /> },
        // No reading — the chain unread, an activation on its way, not read yet: why, as a tag of its own.
        ...(!hasReading(w) ? [{ label: 'State', value: <StateWord w={w} d={d} /> }] : []),
        { label: 'Rented', value: Number(w.rentedEnergy) > 0 ? <EnergyFigure n={w.rentedEnergy} /> : <span className="text-tg-hint">—</span> },
        ...(Number(w.rentedEnergy) > 0 && w.nextExpiry
          ? [{ label: 'Ends', value: <span title={endsIn(secondsUntil(w.nextExpiry, d.asOf))}>{fmtStamp(w.nextExpiry)}</span> }] : []),
        // An order on its way, or one that got no answer: its state, a fact of its own.
        ...(w.unresolved ? [{ label: 'Order', value: <ToneWord tone={STATE_TONE[w.unresolved.state]} word={STATE_WORD[w.unresolved.state]} /> }] : []),
      ]}
      help={<AboutStanding d={d} />}
    />
  );

  return (
    <FormFrame title="Wallet energy settings" aside={besideForm(aside)} below={<>{underForm(aside)}{back}</>}>
      <DeskLines desk={desk} />
      {d && !w && <StateLine tone="warn" action={back}>The desk lists no such wallet.</StateLine>}
      {d && w && !canChange && <StateLine tone="neutral" action={back}>Changing a wallet needs full access.</StateLine>}
      {d && w && canChange && <SettingsForm key={w.id} w={w} d={d} />}
    </FormFrame>
  );
}

/** A wallet's standing allocation and status — its name the register's — Energy.tsx's useEditWallet and its lines, in Astra's form anatomy. */
function SettingsForm({ w, d }: { w: EnergyWallet; d: EnergyStatus }) {
  const navigate = useNavigate();
  // v2: Astra's review step — the readback of what Save will send. Any edit takes it back to the fields; so does a refusal.
  const [reviewing, setReviewing] = useState(false);
  const e = useEditWallet({ w, d, onDone: () => navigate('/energy'), onRefused: () => setReviewing(false) });
  const { standing, setStanding, problem, setProblem, save, a, busy, unsaved, hold } = e;

  const review = () => {
    const c = e.checked();
    if ('problem' in c) { setProblem(c.problem); return; }
    setProblem(null);
    setReviewing(true);
  };
  const edit = <T,>(set: (v: T) => void) => (v: T) => { setReviewing(false); set(v); };
  const c = e.checked();
  const change = 'change' in c ? c.change : null;
  const readback = change === null ? null
    : Object.keys(change).length === 0 ? 'Nothing has changed.'
      : [change.standingEnergy !== undefined ? `Standing: ${units(w.standingEnergy)} → ${units(change.standingEnergy)}` : null].filter(Boolean).join(' · ');

  return (
    <>
      <div>
        <Fact label="Wallet"><span className="break-words">{nameOf(d, w)}</span></Fact>
        <Fact label="Address"><span className="whitespace-nowrap font-mono">{mask(w.address)}</span></Fact>
      </div>
      <EditHold e={e} w={w} d={d} />
      <FieldRow>
        <Field label="Transfers a day">
          <TransfersADay e={e} d={d} onPick={edit(setStanding)} />
        </Field>
        <Field label="Standing energy" hint="0 for none">
          <input className="input font-mono" inputMode="numeric" value={standing} disabled={busy} onChange={(ev) => edit(setStanding)(ev.target.value)} />
        </Field>
      </FieldRow>
      <StandingCost e={e} />

      {/* STATUS — its own call to the desk, as in Energy.tsx: Pause and Make active go at once; Retire is confirmed. */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-stroke-soft pt-3">
        <span className="text-body">Status <span className="ml-2 font-medium">{w.status === 'active' ? 'Active' : w.status === 'paused' ? 'Paused' : 'Retired'}</span></span>
        <span className="flex flex-wrap gap-2">
          {w.status === 'active'
            ? <button type="button" className="btn-secondary px-3 text-body disabled:opacity-50" disabled={busy} onClick={e.pause}>Pause</button>
            : <button type="button" className="btn-secondary px-3 text-body disabled:opacity-50" disabled={busy || hold !== null}
                title={hold ?? undefined} onClick={e.activate}>Make active</button>}
          {w.status !== 'retired' && (
            <button type="button" className="btn-secondary px-3 text-body text-neg disabled:opacity-50" disabled={busy} onClick={e.retire}>
              Retire
            </button>
          )}
        </span>
      </div>

      {/* ARRANGE IT NOW — the SAVED figure, by name; the same order again only for that figure. */}
      {e.arrangeShown && (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className="btn-secondary px-3 text-body disabled:opacity-50" disabled={e.arrangeOff}
            title={e.arrangeWhy} onClick={() => void e.arrange()}>
            {e.arrangeWord}
          </button>
          {unsaved && <span className="text-meta text-tg-hint">{ARRANGE_UNSAVED}</span>}
        </div>
      )}
      {/* words:ok — the desk's or the Books' own refusal, first clause on the line (Said) */}
      {problem !== null ? <Said text={problem} /> : save.isError ? <Said text={errorWords(save.error)} /> : null}
      <ArrangeNoAnswer e={e} rentalsHere={false} />
      <Outcome outcome={a.outcome} atMs={a.saidAtMs} rentals={d.rentals} rentalsHere={false} slabs={V2_SLABS} />

      <FormFoot note={reviewing ? readback : 'Review once before saving.'}>
        {reviewing ? (
          <>
            <button type="button" className={QUIET} disabled={save.isPending} onClick={() => setReviewing(false)}>Edit</button>
            <button type="button" className={`${PRIMARY} disabled:opacity-50`} disabled={busy} onClick={e.submit}>{save.isPending ? 'Saving…' : 'Save settings'}</button>
          </>
        ) : (
          <button type="button" className={`${PRIMARY} disabled:opacity-50`} disabled={busy} onClick={review}>Review settings →</button>
        )}
      </FormFoot>
    </>
  );
}
